// @flow
import type {SagaIterator} from 'redux-saga';
import {propEq} from 'ramda';

import type {ActionType} from 'constants/flow/flowTypes';
import {CATEGORY} from 'constants/actions';
import {CATEGORY_STORE} from 'constants/category';

import {sessionStore} from 'libs/storage/storage';
import {getActiveSort, getCategory, getCategoryId} from 'selectors/category/category';
import {takeEvery, call, select, put, fork, putResolve} from 'redux-saga/effects';
import {checkIsProductIdInMyFit, setActiveWindowProduct} from 'actions/product/product';
import {selectedProduct} from 'actions/analytics/analytics';
import {setProductInfo} from 'features/tryOn/reducers/collection';
import type {
    ProductPayloadType,
    PreloadedProductPayloadType,
} from 'features/categoryRefinements/reducers/category/categoryTypes';
import {
    savePreloadedProduct,
    openTryOn,
} from 'features/categoryRefinements/reducers/category/category';
import {categoryGASaga} from 'features/categoryRefinements/sagas/category/analytics/GA';
import {getActiveFilters} from 'selectors/filters/filters';
import {getCategoryItem, getCategoryItemCount} from 'actions/category/category';
import {getCurrentGender} from 'selectors/gender/gender';
import {productAdapter} from 'reducers/product/productAdapter/productAdapter';

/**
 * On category route save
 */
export function* onCategoryRouteSave(): SagaIterator<> {
    const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const category = yield select(getCategory);
    const {genderId} = yield select(getCurrentGender);

    yield call([sessionStore, 'set'], CATEGORY_STORE, {
        url,
        id: category.id,
        path: window.location.pathname,
        search: window.location.search,
        name: category.title,
        gender: genderId,
    });
}

/**
 * Preloaded product save
 * @param {ActionType<PreloadedProductPayloadType>} action - dispatched action
 */
export function* onPreloadedProductSave(
    action: ActionType<PreloadedProductPayloadType>,
): SagaIterator<> {
    const children = action.payload.product.children.find(propEq('id', action.payload.childrenId));
    if (children) {
        yield putResolve(setActiveWindowProduct(productAdapter(action.payload.product)));
        yield put(checkIsProductIdInMyFit(children.id));
        yield put(selectedProduct({...children, index: action.payload.index}));
    }
}

/**
 * Opens try on window
 * @param {ActionType<ProductPayloadType>} action - dispatched action
 */
export function* onCategoryTryOnOpen({
    payload: {product, childrenId},
}: ActionType<ProductPayloadType>): SagaIterator {
    const children = product.children.find(propEq('id', childrenId));
    if (children) {
        yield put(setProductInfo(children.id, children.name, children.url));
    }
}

/**
 * Fetches sorted category items.
 */
export function* fetchSortedItems(): SagaIterator<> {
    const categoryId = yield select(getCategoryId);
    const activeSort = yield select(getActiveSort);
    const activeFilters = yield select(getActiveFilters);

    const options = yield {
        append: false,
        page: 1,
        dir: activeSort.value,
        order: activeSort.option,
    };

    const itemsAction = yield call(getCategoryItem, categoryId, activeFilters, options);
    yield put(itemsAction);

    const itemsCountAction = yield call(getCategoryItemCount, categoryId, activeFilters, options);
    yield put(itemsCountAction);
}

/**
 * Category saga
 */
export function* categorySaga(): SagaIterator<> {
    yield takeEvery(CATEGORY.GET_ACTIVE_SORT, fetchSortedItems);
    yield takeEvery(
        [`${CATEGORY.GET_CATEGORY}_FULFILLED`, CATEGORY.SET_ACTIVE_FILTERS],
        onCategoryRouteSave,
    );
    yield takeEvery(savePreloadedProduct, onPreloadedProductSave);
    yield takeEvery(openTryOn, onCategoryTryOnOpen);
    yield fork(categoryGASaga);
}
